import React from 'react';
import { Share2, TrendingUp, Users, Gift, Copy, Check, ExternalLink, Plus, Mail } from 'lucide-react';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import { Modal } from '../components/ui/Modal';
import { Input } from '../components/ui/Input';
import { Textarea } from '../components/ui/Textarea';
import { useTenant } from '../contexts/TenantContext';
import { motion } from 'motion/react';
import { safeFormat, cn } from '../lib/utils';
import { useReferrals, useGenerateReferral, useSendReferralEmail } from '../hooks/useReferrals';
import { usePermissions } from '../hooks/usePermissions';
import { toast } from 'react-hot-toast';

export const ReferralsPage = () => {
  const { node, brand } = useTenant();
  const { data: referrals = [], isLoading } = useReferrals(node?.id || '');
  const generateReferral = useGenerateReferral(node?.id || '');
  const { can } = usePermissions();
  
  const [copied, setCopied] = React.useState(false);
  const [isEmailModalOpen, setIsEmailModalOpen] = React.useState(false);
  const [emailRecipients, setEmailRecipients] = React.useState('');
  const [personalMessage, setPersonalMessage] = React.useState('');
  
  // Get the most recent referral code for the copy link, if any
  const activeReferral = referrals[0];
  const referralCode = activeReferral?.referralCode;
  const referralLink = referralCode ? `${window.location.origin}/sign-up?ref=${referralCode}` : '';

  const handleCopy = () => {
    if (!referralLink) return;
    navigator.clipboard.writeText(referralLink);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const sendReferralEmail = useSendReferralEmail(node?.id || '', referralCode || '');

  const handleSendEmail = () => {
    const emails = emailRecipients.split(',').map(e => e.trim()).filter(e => e);
    if (emails.length === 0) {
      toast.error('Please enter at least one email address.');
      return;
    }
    
    sendReferralEmail.mutate(
      { emails, personalMessage: personalMessage.trim() || undefined },
      {
        onSuccess: () => {
          setIsEmailModalOpen(false);
          setEmailRecipients('');
          setPersonalMessage('');
        }
      }
    );
  };

  const totalCredits = referrals.reduce((acc, curr) => acc + curr.creditsEarned, 0);
  const canManage = can('manage_referrals');

  return (
    <div className="max-w-7xl mx-auto space-y-8 pb-12">
      <header className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">Refer & Earn</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Help the network grow and earn credits toward your organization's subscription.
          </p>
        </div>
        {canManage && (
          <Button 
            variant="primary" 
            className="rounded-none shadow-[4px_4px_0px_#000000] dark:shadow-[4px_4px_0px_#FFFFFF] h-11 px-6 bg-navy-900 hover:bg-navy-800 text-white border-none gap-2"
            onClick={() => generateReferral.mutate()}
            isLoading={generateReferral.isPending}
          >
            <Plus size={18} />
            Generate New Link
          </Button>
        )}
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Referral Card */}
        <Card className="lg:col-span-2 overflow-hidden border-2 border-brand-primary/10">
          <div className="flex flex-col md:flex-row gap-8 items-center">
            <div className="flex-1 space-y-4">
              <div className="p-2 bg-brand-primary/10 rounded-none w-fit text-brand-primary">
                <Gift size={24} />
              </div>
              <h2 className="text-xl font-bold text-gray-900 dark:text-white">Refer a Peer Organization</h2>
              <p className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed">
                Know another chapter, club, or association that could benefit from {brand?.name}? 
                Share your link, and when they upgrade to a paid tier, your organization 
                gets <span className="font-bold text-gray-900 dark:text-white">$50.00 in total credits</span> apply to your next invoice.
              </p>
              
              <div className="space-y-2 pt-4">
                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest font-mono">Your unique referral link</p>
                <div className="flex gap-2">
                  <div className={cn(
                    "flex-1 bg-white border-2 border-black dark:border-white rounded-none px-4 py-3 font-mono text-xs text-gray-600 dark:text-gray-300 truncate dark:bg-slate-950",
                    !referralLink && "italic text-gray-400"
                  )}>
                    {referralLink || "No referral link generated yet."}
                  </div>
                  <Button 
                    variant="primary" 
                    className="flex-shrink-0 gap-2 h-[42px] bg-brand-primary text-navy-900 border-none hover:bg-brand-primary/90"
                    onClick={() => setIsEmailModalOpen(true)}
                    disabled={!referralLink}
                  >
                    <Mail size={16} />
                    Email Invite
                  </Button>
                  <Button 
                    variant="secondary" 
                    className="flex-shrink-0 gap-2 h-[42px]"
                    onClick={handleCopy}
                    disabled={!referralLink}
                  >
                    {copied ? <Check size={16} className="text-green-600" /> : <Copy size={16} />}
                  </Button>
                </div>
              </div>
            </div>
            
            <div className="flex-shrink-0 p-8 bg-white rounded-none border-2 border-black dark:border-white flex flex-col items-center min-w-[240px] dark:bg-slate-950">
              <div className="w-24 h-24 rounded-none bg-white border-4 border-white shadow-[4px_4px_0px_#000000] dark:shadow-[4px_4px_0px_#FFFFFF] flex items-center justify-center text-brand-primary mb-4 dark:bg-slate-900">
                <TrendingUp size={40} />
              </div>
              <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest font-mono text-center mb-1">Growth Credit Balance</p>
              <p className="text-3xl font-bold font-mono text-navy-900 dark:text-white">${(totalCredits / 100).toFixed(2)}</p>
              <p className="text-[10px] text-gray-500 dark:text-gray-400 text-center mt-3 max-w-[180px] leading-tight font-mono">
                Applied automatically as balance discounts on upcoming billing cycles.
              </p>
            </div>
          </div>
        </Card>

        {/* Info Card */}
        <Card className="bg-navy-900 text-white border-none shadow-[4px_4px_0px_#000000] dark:shadow-[4px_4px_0px_#FFFFFF]">
          <h3 className="font-semibold mb-4 flex items-center gap-2">
            <Share2 size={18} className="text-brand-primary" />
            How it works
          </h3>
          <div className="space-y-6">
            <div className="flex gap-3">
              <div className="w-6 h-6 rounded-none bg-navy-800 flex items-center justify-center text-xs font-bold font-mono border-2 border-black dark:border-white border-navy-700">1</div>
              <p className="text-xs text-navy-200 leading-relaxed">Share your unique link with leaders of other organizations.</p>
            </div>
            <div className="flex gap-3">
              <div className="w-6 h-6 rounded-none bg-navy-800 flex items-center justify-center text-xs font-bold font-mono border-2 border-black dark:border-white border-navy-700">2</div>
              <p className="text-xs text-navy-200 leading-relaxed">They sign up and start managing their node (Free tier supports onboarding!).</p>
            </div>
            <div className="flex gap-3">
              <div className="w-6 h-6 rounded-none bg-navy-800 flex items-center justify-center text-xs font-bold font-mono border-2 border-black dark:border-white border-navy-700">3</div>
              <p className="text-xs text-navy-200 leading-relaxed">Once they upgrade to <span className="text-white font-semibold">Professional or Network</span>, your credit is unlocked.</p>
            </div>
          </div>
          <div className="mt-8 pt-6 border-t border-navy-800">
            <Button 
              className="w-full bg-brand-primary hover:bg-brand-primary/90 text-navy-900 dark:text-white border-none font-bold" 
              onClick={() => {
                if (referralLink) {
                  navigator.clipboard.writeText(referralLink);
                  toast.success('Your referral link has been copied to your clipboard!');
                } else {
                  toast.success('Referral link generated. Share it with your community.');
                }
              }}
            >
              Copy Referral Link
            </Button>
          </div>
        </Card>
      </div>

      <Card>
        <div className="flex items-center justify-between mb-6">
          <h3 className="font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
            <Users size={18} className="text-gray-400" />
            Your Referrals
          </h3>
          <Badge variant="gray">{referrals.length} Total</Badge>
        </div>
        
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead>
              <tr className="border-b border-black dark:border-white uppercase font-mono text-[10px] text-gray-400 tracking-widest">
                <th className="pb-4 pt-2 font-medium">Organization</th>
                <th className="pb-4 pt-2 font-medium">Joined Date</th>
                <th className="pb-4 pt-2 font-medium">Status</th>
                <th className="pb-4 pt-2 font-medium">Credits Earned</th>
                <th className="pb-4 pt-2 font-medium text-right">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {referrals.map((ref) => (
                <tr key={ref.id} className="group hover:bg-white/50 transition-colors dark:bg-slate-950">
                  <td className="py-4">
                    <p className="font-medium text-gray-900 dark:text-white">{ref.referredNodeName || "Awaiting Org Name"}</p>
                    <p className="text-[10px] text-gray-400 font-mono">Code: {ref.referralCode}</p>
                  </td>
                  <td className="py-4 text-gray-500 dark:text-gray-400 font-mono text-xs">
                    {safeFormat(ref.createdAt, 'MMM d, yyyy')}
                  </td>
                  <td className="py-4">
                    <Badge variant={ref.status === 'ACCEPTED' ? 'green' : ref.status === 'PENDING' ? 'amber' : 'red'}>
                      {ref.status}
                    </Badge>
                  </td>
                  <td className="py-4">
                    <p className={ref.creditsEarned > 0 ? "font-bold text-gray-900" : "text-gray-400"}>
                      {ref.creditsEarned > 0 ? `$${(ref.creditsEarned / 100).toFixed(2)}` : "—"}
                    </p>
                  </td>
                  <td className="py-4 text-right">
                    {ref.status === 'ACCEPTED' && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="opacity-0 group-hover:opacity-100"
                        onClick={() => {
                          if ((ref as any).referredNodeSlug) {
                            window.location.href = `/orgs/${(ref as any).referredNodeSlug}`;
                          } else {
                            window.location.href = '/global';
                          }
                        }}
                      >
                        <ExternalLink size={14} className="mr-1" /> View Org
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
              {referrals.length === 0 && (
                <tr>
                  <td colSpan={5} className="py-12 text-center text-gray-400 text-sm italic">
                    No referrals tracked yet. Generate a link above to get started!
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>

      <Modal
        isOpen={isEmailModalOpen}
        onClose={() => setIsEmailModalOpen(false)}
        title="Send Email Invitations"
      >
        <div className="space-y-4 pt-2">
          <p className="text-sm text-gray-500">
            Send an invitation to join OrgSets. You can enter multiple email addresses separated by commas.
          </p>
          <div className="space-y-2">
            <Input
              label="Recipient Emails (comma-separated)"
              placeholder="e.g. admin@school.edu, coach@team.com"
              value={emailRecipients}
              onChange={(e) => setEmailRecipients(e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <label className="text-xs font-semibold text-gray-600 dark:text-gray-400">Personal Message (optional)</label>
            <Textarea
              placeholder="Add a personal note to the email..."
              value={personalMessage}
              onChange={(e) => setPersonalMessage(e.target.value)}
              rows={3}
            />
          </div>
          <div className="flex justify-end gap-3 pt-4 border-t border-black dark:border-white dark:border-white">
            <Button variant="ghost" onClick={() => setIsEmailModalOpen(false)}>
              Cancel
            </Button>
            <Button 
              variant="primary" 
              onClick={handleSendEmail}
              isLoading={sendReferralEmail.isPending}
              disabled={!emailRecipients.trim()}
            >
              Send Invitations
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};
